import { bitmapContentSize, CAPTURE_VEIL_ALPHA } from '@hibro/shared';
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  ArrowGlyph,
  BrushGlyph,
  CancelGlyph,
  CopyGlyph,
  DownloadGlyph,
  MosaicGlyph,
  OkGlyph,
  RedoGlyph,
  TextGlyph,
  ToolDivider,
  UndoGlyph,
} from './capture-glyphs';
import { captureHostWindow, logCapture } from './capture-host';
import {
  type Annotation,
  type ArrowEnd,
  clampTextOrigin,
  containsPoint,
  extendMosaicTiles,
  type Handle,
  hitAnnotationIndex,
  hitArrowEnd,
  hitHandle,
  isUsableSelection,
  mosaicTileSize,
  moveAnnotationAt,
  moveSelection,
  normalizeRect,
  placeArrowEnd,
  type Point,
  pointerMovedEnough,
  type Rect,
  resizeBoxAnnotation,
  resizeSelection,
  TEXT_REFERENCE_GLYPH,
  textColumnWidth,
  textFont,
  translateAnnotations,
} from './geometry';
import {
  cloneSnapshot,
  type EditorSnapshot,
  emptyHistory,
  recordEdit,
  redoEdit,
  undoEdit,
} from './history';
import { exportSelectionPng, strokeAnnotations } from './paint';

/**
 * Paint colors for Figma `421:37511`. Swatches use the token; the canvas stores
 * the resolved value. `#858585` (`421:37542`) has no variable.
 */
const INK_TOKENS = [
  { token: '--app-base-extended-red' },
  { token: '--app-base-extended-orange' },
  { token: '--app-base-extended-yellow' },
  { token: '--app-base-extended-green' },
  { token: '--app-base-extended-cyan' },
  { token: '--app-base-white', border: '--app-base-gray-86-a90' },
  { value: '#858585' },
  { token: '--app-base-black', border: '--app-base-gray-86' },
] as const;
const STROKES = [3, 6, 9] as const;
/** Visual diameters for the three strokes (`421:37514` / `37517` / `37520`). */
const STROKE_DOT: Record<(typeof STROKES)[number], string> = {
  3: 'var(--app-base-size-5)',
  6: 'var(--app-base-size-7)',
  9: 'var(--app-base-size-22px)',
};
const STROKE_FONTS = [
  { stroke: 3, fontSize: 18 },
  { stroke: 6, fontSize: 32 },
  { stroke: 9, fontSize: 46 },
] as const;
const HANDLE_RADIUS = 5;
/** 24px hit box so the resize cursor appears before the pointer is on the dot. */
const HANDLE_HIT_RADIUS = 12;
/** `421:36245` preview is 136×136. Sampling stays 1:1 with that viewport. */
const LOUPE_SIZE = 136;
const LOUPE_GAP = 20;
/**
 * Crosshair from the `421:36245` overlay. Strokes are 1px and centered on
 * these path coordinates (`#6366f1` → `--app-base-brand`).
 */
const LOUPE_CROSS_X = 68.5062;
const LOUPE_CROSS_Y = 67.4938;
const TOOLBAR_GAP = 10;
/** `421:37511` bar: 6 + 36 + 6 padding plus the 1px border on each side. */
const OPTION_BAR_HEIGHT = 50;
/**
 * Figma `421:37450` / `421:37511`.
 * Fill is `Color/background/pop/pop S` (`--app-base-white-a85`) plus a background
 * blur of 30 (CSS 15px). Blur is omitted on this image overlay; alpha is 0.95.
 * Shadow is `0 0 12px #00000014` (`--app-base-black-a8`). There is no 0.95 token.
 */
const BAR_CHROME = {
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 0 var(--app-base-size-7) var(--app-base-black-a8)',
} as const;
const BAR_CLASS =
  'flex items-center border border-[var(--app-base-white-a60)] px-[var(--app-base-size-7)] py-[var(--app-base-size-4)]';
const TOOL_BUTTON =
  'inline-flex size-[var(--app-base-size-15)] shrink-0 cursor-pointer items-center justify-center rounded-[var(--app-base-radius-10)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-base-brand)] enabled:hover:bg-[var(--app-base-black-a4)] disabled:cursor-not-allowed disabled:opacity-40';

type Tool = 'move' | 'rect' | 'ellipse' | 'arrow' | 'brush' | 'text' | 'mosaic';

type Drag =
  | { kind: 'new'; origin: Point }
  | { kind: 'move'; last: Point }
  | { kind: 'resize'; handle: Handle }
  | { kind: 'rect'; origin: Point }
  | { kind: 'ellipse'; origin: Point }
  | { kind: 'arrow'; origin: Point }
  | { kind: 'brush' }
  | { kind: 'mosaic' }
  | { kind: 'shape-resize'; index: number; handle: Handle }
  | { kind: 'arrow-end'; index: number; end: ArrowEnd }
  | {
      kind: 'annotation';
      index: number;
      origin: Point;
      last: Point;
      moved: boolean;
      tool: Tool;
    };

interface TextSession {
  index: number | null;
  x: number;
  y: number;
  /** Null during the first edit, while the column still follows the right edge. */
  wrapWidth: number | null;
  toolbarColor: string;
  toolbarStroke: number;
}

interface Labels {
  hint: string;
  ok: string;
  copy: string;
  download: string;
  cancel: string;
  move: string;
  rect: string;
  ellipse: string;
  arrow: string;
  text: string;
  brush: string;
  mosaic: string;
  undo: string;
  redo: string;
  copyFailed: string;
  saveFailed: string;
  coordinates: string;
  rgb: string;
  copyColor: string;
}

function labels(): Labels {
  const mac = /Mac|iPhone|iPad/i.test(navigator.platform);
  const mod = mac ? '⌘' : 'Ctrl';
  const redoShortcut = mac ? `${mod}+Shift+Z` : `${mod}+Y`;
  const zh = navigator.language.toLowerCase().startsWith('zh');
  if (zh) {
    return {
      hint: '拖拽选择区域',
      ok: '确定',
      copy: '复制',
      download: '下载',
      cancel: '取消',
      move: '选区',
      rect: '矩形',
      ellipse: '椭圆',
      arrow: '箭头',
      text: '文字',
      brush: '画笔',
      mosaic: '马赛克',
      undo: `撤销 (${mod}+Z)`,
      redo: `重做 (${redoShortcut})`,
      copyFailed: '复制到剪贴板失败',
      saveFailed: '保存失败',
      coordinates: '坐标',
      rgb: 'RGB',
      copyColor: '按 C 复制颜色',
    };
  }
  return {
    hint: 'Drag to select a region',
    ok: 'OK',
    copy: 'Copy',
    download: 'Download',
    cancel: 'Cancel',
    move: 'Select',
    rect: 'Rectangle',
    ellipse: 'Ellipse',
    arrow: 'Arrow',
    text: 'Text',
    brush: 'Brush',
    mosaic: 'Mosaic',
    undo: `Undo (${mod}+Z)`,
    redo: `Redo (${redoShortcut})`,
    copyFailed: 'Could not copy to the clipboard',
    saveFailed: 'Could not save the image',
    coordinates: 'Coordinates',
    rgb: 'RGB',
    copyColor: 'Press C to copy color',
  };
}

function readToken(name: string): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  if (!value) {
    throw new Error(`capture toolbar token ${name} is missing`);
  }
  return value;
}

interface InkChoice {
  paint: string;
  swatch: string;
  border?: string;
}

function inkChoices(): InkChoice[] {
  return INK_TOKENS.map((item) => {
    if ('value' in item) {
      return { paint: item.value, swatch: item.value };
    }
    return {
      paint: readToken(item.token),
      swatch: `var(${item.token})`,
      ...('border' in item ? { border: `var(${item.border})` } : {}),
    };
  });
}

function fontSizeForStroke(stroke: number): number {
  const match = STROKE_FONTS.find((item) => item.stroke === stroke);
  if (!match) {
    throw new Error(`unknown capture stroke ${stroke}`);
  }
  return match.fontSize;
}

function strokeForFontSize(fontSize: number): number {
  const match = STROKE_FONTS.find((item) => item.fontSize === fontSize);
  if (!match) {
    throw new Error(`unknown capture font size ${fontSize}`);
  }
  return match.stroke;
}

export interface CaptureEditorProps {
  png: Uint8Array;
  imageWidth: number;
  imageHeight: number;
  /** Display scale already used to size this window's client area. */
  scaleFactor: number;
  /** Return false to keep the editor open (clipboard write failed). */
  onConfirm: (png: Uint8Array) => void | Promise<void | boolean>;
  /** Write PNG bytes. Return false when the clipboard refused them. */
  onCopy: (png: Uint8Array) => Promise<boolean>;
  onCancel: () => void;
  /** The bitmap has decoded at its 1:1 CSS size. */
  onPainted: () => void;
  /** The bitmap failed to decode. */
  onPaintFailed: () => void;
}

/**
 * Region editor drawn on a frozen frame. Selection stays in this view; the
 * caller supplies one frame per display so a rectangle cannot cross screens.
 */
export function CaptureEditor({
  png,
  imageWidth,
  imageHeight,
  scaleFactor,
  onConfirm,
  onCopy,
  onCancel,
  onPainted,
  onPaintFailed,
}: CaptureEditorProps) {
  const text = labels();
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [url] = useState(() =>
    URL.createObjectURL(new Blob([png as BlobPart], { type: 'image/png' })),
  );
  const [selection, setSelection] = useState<Rect | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [draft, setDraft] = useState<Annotation | null>(null);
  const [tool, setTool] = useState<Tool>('move');
  const [color, setColor] = useState(() => {
    const first = inkChoices()[0];
    if (!first) {
      throw new Error('capture ink palette is empty');
    }
    return first.paint;
  });
  const [stroke, setStroke] = useState<number>(STROKES[0]);
  const [fontSize, setFontSize] = useState(fontSizeForStroke(STROKES[0] ?? 3));
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [loupe, setLoupe] = useState<Point | null>(null);
  const [textEdit, setTextEdit] = useState<TextSession | null>(null);
  const [textValue, setTextValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState('crosshair');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const dragRef = useRef<Drag | null>(null);
  const draftRef = useRef(draft);
  const historyRef = useRef(emptyHistory());
  const baselineRef = useRef<EditorSnapshot | null>(null);
  const undoRef = useRef<() => void>(() => {});
  const redoRef = useRef<() => void>(() => {});
  const hoverRef = useRef<Point | null>(null);
  const selectionRef = useRef(selection);
  const annotationsRef = useRef(annotations);
  const textEditRef = useRef(textEdit);
  const textValueRef = useRef(textValue);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cancelTextRef = useRef<() => void>(() => {});
  const colorRef = useRef(color);
  const fontSizeRef = useRef(fontSize);
  const strokeRef = useRef(stroke);
  const toolRef = useRef(tool);
  const selectedRef = useRef(selectedIndex);
  const imageDataRef = useRef<ImageData | null>(null);
  const layoutRef = useRef<{ width: number; height: number } | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarSize, setToolbarSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  selectionRef.current = selection;
  annotationsRef.current = annotations;
  draftRef.current = draft;
  textEditRef.current = textEdit;
  textValueRef.current = textValue;
  colorRef.current = color;
  fontSizeRef.current = fontSize;
  strokeRef.current = stroke;
  toolRef.current = tool;
  selectedRef.current = selectedIndex;

  const measureWidth = (text: string, size: number): number => {
    const canvas = measureCanvasRef.current ?? document.createElement('canvas');
    measureCanvasRef.current = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('canvas is not available');
    }
    ctx.font = textFont(size);
    return ctx.measureText(text).width;
  };

  const onPaintedRef = useRef(onPainted);
  onPaintedRef.current = onPainted;
  const [layoutSize, setLayoutSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  layoutRef.current = layoutSize;

  useEffect(() => {
    return () => URL.revokeObjectURL(url);
  }, [url]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) {
      return;
    }
    const publish = (): void => {
      const rect = frame.getBoundingClientRect();
      setLayoutSize((current) => {
        if (
          current &&
          current.width === rect.width &&
          current.height === rect.height
        ) {
          return current;
        }
        return { width: rect.width, height: rect.height };
      });
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth > 0) {
      rememberPixels(image);
      onPaintedRef.current();
    }
  }, [url]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layoutSize) {
      return;
    }
    if (!(layoutSize.width > 0) || !(layoutSize.height > 0)) {
      return;
    }
    const backingWidth = Math.round(layoutSize.width * scaleFactor);
    const backingHeight = Math.round(layoutSize.height * scaleFactor);
    if (backingWidth <= 0 || backingHeight <= 0) {
      return;
    }
    canvas.width = backingWidth;
    canvas.height = backingHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.setTransform(scaleFactor, 0, 0, scaleFactor, 0, 0);
    ctx.clearRect(0, 0, layoutSize.width, layoutSize.height);
    const preview = draft ? [draft] : [];
    const painted =
      textEdit?.index == null
        ? annotations
        : annotations.filter((_, index) => index !== textEdit.index);
    ctx.save();
    ctx.fillStyle = `rgba(0, 0, 0, ${CAPTURE_VEIL_ALPHA})`;
    ctx.fillRect(0, 0, layoutSize.width, layoutSize.height);
    if (selection) {
      ctx.clearRect(
        selection.x,
        selection.y,
        selection.width,
        selection.height,
      );
      ctx.strokeStyle = '#0090ff';
      ctx.lineWidth = 1;
      ctx.strokeRect(
        selection.x,
        selection.y,
        selection.width,
        selection.height,
      );
      if (tool === 'move') {
        drawHandleDots(ctx, selection);
      }
    }
    ctx.restore();
    if (selection) {
      const shapes = painted.filter((item) => item.kind !== 'text');
      const texts = painted.filter((item) => item.kind === 'text');
      strokeAnnotations(ctx, shapes, selection);
      strokeAnnotations(ctx, preview, selection);
      strokeAnnotations(ctx, texts, selection);
    } else {
      strokeAnnotations(ctx, painted, null);
      strokeAnnotations(ctx, preview, null);
    }
    const selected = selectedIndex == null ? null : annotations[selectedIndex];
    if (
      selected &&
      (selected.kind === 'rect' || selected.kind === 'ellipse') &&
      tool === selected.kind
    ) {
      drawHandleDots(ctx, selected);
    }
    if (selected?.kind === 'arrow' && tool === 'arrow') {
      drawHandleDot(ctx, { x: selected.x1, y: selected.y1 });
      drawHandleDot(ctx, { x: selected.x2, y: selected.y2 });
    }
  }, [
    annotations,
    draft,
    layoutSize,
    scaleFactor,
    selectedIndex,
    selection,
    textEdit,
    tool,
  ]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (textEditRef.current) {
          cancelTextRef.current();
          return;
        }
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.isComposing) {
        return;
      }
      const mod = event.metaKey || event.ctrlKey;
      if (!mod || event.altKey) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key !== 'z' && key !== 'y') {
        return;
      }
      if (textEditRef.current) {
        if (!isEditableTarget(event.target)) {
          event.preventDefault();
        }
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undoRef.current();
        return;
      }
      if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        redoRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    if (!textEdit) {
      return;
    }
    textInputRef.current?.focus();
  }, [textEdit]);

  useLayoutEffect(() => {
    const field = textInputRef.current;
    if (!field) {
      return;
    }
    // `overflow: visible` on a textarea computes to `auto`, so a wrapped line
    // grows a scrollbar and the scrollbar steals width. Size to the content.
    field.style.height = '0px';
    field.style.height = `${field.scrollHeight}px`;
  }, [fontSize, selection, textEdit, textValue]);

  useEffect(() => {
    setCursor(
      pointerCursor(
        tool,
        selection,
        hoverRef.current,
        dragRef.current,
        annotations,
        measureWidth,
        selectedIndex == null ? null : (annotations[selectedIndex] ?? null),
      ),
    );
  }, [annotations, selectedIndex, selection, tool]);

  const pointOf = (event: { clientX: number; clientY: number }): Point => {
    const rect = frameRef.current?.getBoundingClientRect();
    return {
      x: event.clientX - (rect?.left ?? 0),
      y: event.clientY - (rect?.top ?? 0),
    };
  };

  const exportPng = async (): Promise<Uint8Array | null> => {
    const current = selectionRef.current;
    const image = imageRef.current;
    const frame = frameRef.current;
    if (!current || !isUsableSelection(current) || !image || !frame) {
      return null;
    }
    const view = frame.getBoundingClientRect();
    return exportSelectionPng({
      source: image,
      imageWidth,
      imageHeight,
      viewWidth: view.width,
      viewHeight: view.height,
      selection: current,
      annotations: annotationsRef.current,
    });
  };

  const takeSnapshot = (): EditorSnapshot =>
    cloneSnapshot({
      selection: selectionRef.current,
      annotations: annotationsRef.current,
    });

  const syncHistoryFlags = (): void => {
    setCanUndo(historyRef.current.past.length > 0);
    setCanRedo(historyRef.current.future.length > 0);
  };

  const applySnapshot = (snapshot: EditorSnapshot): void => {
    const next = cloneSnapshot(snapshot);
    selectionRef.current = next.selection;
    annotationsRef.current = next.annotations;
    draftRef.current = null;
    setSelection(next.selection);
    setAnnotations(next.annotations);
    setDraft(null);
  };

  const beginHistory = (): void => {
    baselineRef.current = takeSnapshot();
  };

  const finishHistory = (): void => {
    const before = baselineRef.current;
    baselineRef.current = null;
    if (!before) {
      return;
    }
    const after = takeSnapshot();
    const next = recordEdit(historyRef.current, before, after);
    if (next === historyRef.current) {
      return;
    }
    historyRef.current = next;
    syncHistoryFlags();
  };

  const finishHistoryWith = (snapshot: EditorSnapshot): void => {
    const before = baselineRef.current ?? takeSnapshot();
    baselineRef.current = null;
    const after = cloneSnapshot(snapshot);
    const next = recordEdit(historyRef.current, before, after);
    if (next !== historyRef.current) {
      historyRef.current = next;
      syncHistoryFlags();
    }
    applySnapshot(after);
  };

  const restoreBaseline = (): void => {
    const before = baselineRef.current;
    baselineRef.current = null;
    if (before) {
      applySnapshot(before);
    }
  };

  const cursorFor = (
    point: Point | null,
    drag: Drag | null,
    rect: Rect | null,
  ): string =>
    pointerCursor(
      toolRef.current,
      rect,
      point,
      drag,
      annotationsRef.current,
      measureWidth,
      selectedRef.current == null
        ? null
        : (annotationsRef.current[selectedRef.current] ?? null),
    );

  const rememberPixels = (image: HTMLImageElement): void => {
    if (!(image.naturalWidth > 0) || !(image.naturalHeight > 0)) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      logCapture('mosaic sample canvas is unavailable');
      return;
    }
    ctx.drawImage(image, 0, 0);
    imageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
  };

  const sampleFrozenPixel = (point: Point): string | null => {
    const data = imageDataRef.current;
    const view = layoutRef.current;
    if (!data || !view || !(view.width > 0) || !(view.height > 0)) {
      return null;
    }
    const x = Math.min(
      data.width - 1,
      Math.max(0, Math.floor((point.x * data.width) / view.width)),
    );
    const y = Math.min(
      data.height - 1,
      Math.max(0, Math.floor((point.y * data.height) / view.height)),
    );
    const index = (y * data.width + x) * 4;
    const red = data.data[index];
    const green = data.data[index + 1];
    const blue = data.data[index + 2];
    const alpha = data.data[index + 3];
    if (
      red === undefined ||
      green === undefined ||
      blue === undefined ||
      alpha === undefined
    ) {
      return null;
    }
    return `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
  };

  const markSelected = (index: number, nextTool: Tool): void => {
    selectedRef.current = index;
    setSelectedIndex(index);
    if (toolRef.current !== nextTool) {
      toolRef.current = nextTool;
      setTool(nextTool);
    }
  };

  const clearSelected = (): void => {
    if (selectedRef.current == null) {
      return;
    }
    selectedRef.current = null;
    setSelectedIndex(null);
  };

  const chooseTool = (next: Tool): void => {
    // `421:37450` has no select button. Clicking the active tool returns to move
    // so the selection can still be dragged and resized.
    const resolved = toolRef.current === next ? 'move' : next;
    if (toolRef.current !== resolved) {
      clearSelected();
    }
    toolRef.current = resolved;
    setTool(resolved);
  };

  const chooseStroke = (next: number): void => {
    const size = fontSizeForStroke(next);
    strokeRef.current = next;
    fontSizeRef.current = size;
    setStroke(next);
    setFontSize(size);
  };

  const closeTextSession = (): void => {
    textEditRef.current = null;
    textValueRef.current = '';
    setTextEdit(null);
    setTextValue('');
  };

  const cancelText = (): void => {
    const session = textEditRef.current;
    if (!session) {
      return;
    }
    colorRef.current = session.toolbarColor;
    strokeRef.current = session.toolbarStroke;
    fontSizeRef.current = fontSizeForStroke(session.toolbarStroke);
    setColor(session.toolbarColor);
    setStroke(session.toolbarStroke);
    setFontSize(fontSizeForStroke(session.toolbarStroke));
    closeTextSession();
  };
  cancelTextRef.current = cancelText;

  const beginTextEdit = (
    index: number | null,
    at: Point,
    value: string,
    nextColor: string,
    nextFontSize: number,
    wrapWidth: number | null,
  ): void => {
    const session: TextSession = {
      index,
      x: at.x,
      y: at.y,
      wrapWidth,
      toolbarColor: colorRef.current,
      toolbarStroke: strokeRef.current,
    };
    textEditRef.current = session;
    textValueRef.current = value;
    setTextEdit(session);
    setTextValue(value);
    const nextStroke = strokeForFontSize(nextFontSize);
    colorRef.current = nextColor;
    strokeRef.current = nextStroke;
    fontSizeRef.current = nextFontSize;
    setColor(nextColor);
    setStroke(nextStroke);
    setFontSize(nextFontSize);
  };

  const undo = (): void => {
    if (dragRef.current || baselineRef.current || textEditRef.current) {
      return;
    }
    const step = undoEdit(historyRef.current, takeSnapshot());
    if (!step) {
      return;
    }
    historyRef.current = step.history;
    applySnapshot(step.present);
    selectedRef.current = null;
    setSelectedIndex(null);
    syncHistoryFlags();
  };

  const redo = (): void => {
    if (dragRef.current || baselineRef.current || textEditRef.current) {
      return;
    }
    const step = redoEdit(historyRef.current, takeSnapshot());
    if (!step) {
      return;
    }
    historyRef.current = step.history;
    applySnapshot(step.present);
    selectedRef.current = null;
    setSelectedIndex(null);
    syncHistoryFlags();
  };
  undoRef.current = undo;
  redoRef.current = redo;

  const commitText = (): void => {
    const session = textEditRef.current;
    if (!session) {
      return;
    }
    const value = textValueRef.current.trim();
    const index = session.index;
    closeTextSession();
    if (!value) {
      if (index === null) {
        return;
      }
      const before = takeSnapshot();
      const selected = selectedRef.current;
      if (selected === index) {
        clearSelected();
      } else if (selected != null && selected > index) {
        selectedRef.current = selected - 1;
        setSelectedIndex(selected - 1);
      }
      finishHistoryWith({
        selection: before.selection,
        annotations: before.annotations.filter(
          (_, itemIndex) => itemIndex !== index,
        ),
      });
      return;
    }
    const before = takeSnapshot();
    const bounds = selectionRef.current;
    const nextItem: Annotation = {
      kind: 'text',
      x: session.x,
      y: session.y,
      text: value,
      color: colorRef.current,
      fontSize: fontSizeRef.current,
      wrapWidth:
        session.wrapWidth ??
        (bounds ? Math.max(0, bounds.x + bounds.width - session.x) : 0),
    };
    const annotations = [...before.annotations];
    if (index === null) {
      annotations.push(nextItem);
    } else {
      annotations[index] = nextItem;
    }
    finishHistoryWith({ selection: before.selection, annotations });
  };

  const confirm = (): void => {
    commitText();
    void exportPng()
      .then(async (bytes) => {
        if (!bytes) {
          return;
        }
        const accepted = await onConfirm(bytes);
        if (accepted === false) {
          setError(text.copyFailed);
        }
      })
      .catch((error: unknown) => {
        logCapture('capture confirm failed', error);
      });
  };

  const copy = (): void => {
    commitText();
    void exportPng()
      .then(async (bytes) => {
        if (!bytes) {
          return;
        }
        const ok = await onCopy(bytes);
        if (!ok) {
          setError(text.copyFailed);
        }
      })
      .catch((error: unknown) => {
        logCapture('capture copy failed', error);
      });
  };

  const download = (): void => {
    commitText();
    void exportPng()
      .then(async (bytes) => {
        if (!bytes) {
          return;
        }
        const capture = captureHostWindow().hibroCapture;
        if (!capture) {
          logCapture('capture save bridge is missing');
          setError(text.saveFailed);
          return;
        }
        const result = await capture.save(bytes);
        if (!result.saved) {
          return;
        }
      })
      .catch((error: unknown) => {
        logCapture('capture save failed', error);
        setError(text.saveFailed);
      });
  };

  const onPointerDown = (event: ReactPointerEvent): void => {
    if (event.button !== 0) {
      return;
    }
    const point = pointOf(event);
    hoverRef.current = point;
    if (textEditRef.current) {
      commitText();
    }
    const current = selectionRef.current;
    const activeTool = toolRef.current;
    if (activeTool !== 'move' && current) {
      const selected = selectedRef.current;
      const selectedItem =
        selected == null ? null : annotationsRef.current[selected];
      if (
        selected != null &&
        selectedItem &&
        (selectedItem.kind === 'rect' || selectedItem.kind === 'ellipse') &&
        hitHandle(selectedItem, point, HANDLE_HIT_RADIUS)
      ) {
        const handle = hitHandle(selectedItem, point, HANDLE_HIT_RADIUS);
        if (handle) {
          event.preventDefault();
          beginHistory();
          dragRef.current = { kind: 'shape-resize', index: selected, handle };
          setCursor(cursorFor(point, dragRef.current, current));
          event.currentTarget.setPointerCapture(event.pointerId);
          return;
        }
      }
      if (selected != null && selectedItem?.kind === 'arrow') {
        const end = hitArrowEnd(selectedItem, point, HANDLE_HIT_RADIUS);
        if (end) {
          event.preventDefault();
          beginHistory();
          dragRef.current = { kind: 'arrow-end', index: selected, end };
          setCursor(cursorFor(point, dragRef.current, current));
          event.currentTarget.setPointerCapture(event.pointerId);
          return;
        }
      }
      const index = annotationGrabIndex(
        current,
        point,
        annotationsRef.current,
        measureWidth,
      );
      const hit = index == null ? null : annotationsRef.current[index];
      if (index != null && hit && hit.kind !== 'mosaic') {
        event.preventDefault();
        markSelected(index, hit.kind);
        const handle =
          hit.kind === 'rect' || hit.kind === 'ellipse'
            ? hitHandle(hit, point, HANDLE_HIT_RADIUS)
            : null;
        const arrowEnd =
          hit.kind === 'arrow'
            ? hitArrowEnd(hit, point, HANDLE_HIT_RADIUS)
            : null;
        let gesture: Drag;
        if (handle) {
          beginHistory();
          gesture = { kind: 'shape-resize', index, handle };
        } else if (arrowEnd) {
          beginHistory();
          gesture = { kind: 'arrow-end', index, end: arrowEnd };
        } else {
          gesture = {
            kind: 'annotation',
            index,
            origin: point,
            last: point,
            moved: false,
            tool: hit.kind,
          };
        }
        dragRef.current = gesture;
        setCursor(cursorFor(point, dragRef.current, current));
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
    }
    if (activeTool === 'text' && current && containsPoint(current, point)) {
      event.preventDefault();
      clearSelected();
      const origin = clampTextOrigin(
        point,
        current,
        measureWidth(TEXT_REFERENCE_GLYPH, fontSizeRef.current),
        fontSizeRef.current,
      );
      beginTextEdit(
        null,
        origin,
        '',
        colorRef.current,
        fontSizeRef.current,
        null,
      );
      return;
    }
    if (current && activeTool === 'move') {
      const handle = hitHandle(current, point, HANDLE_HIT_RADIUS);
      if (handle) {
        beginHistory();
        dragRef.current = { kind: 'resize', handle };
        setCursor(cursorFor(point, dragRef.current, current));
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
      if (containsPoint(current, point)) {
        beginHistory();
        dragRef.current = { kind: 'move', last: point };
        setCursor(cursorFor(point, dragRef.current, current));
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
    }
    if (current && activeTool !== 'move' && containsPoint(current, point)) {
      clearSelected();
      beginHistory();
      const ink = colorRef.current;
      const width = strokeRef.current;
      if (activeTool === 'rect' || activeTool === 'ellipse') {
        dragRef.current = { kind: activeTool, origin: point };
      } else if (activeTool === 'arrow') {
        dragRef.current = { kind: 'arrow', origin: point };
        const next: Annotation = {
          kind: 'arrow',
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y,
          color: ink,
          stroke: width,
        };
        draftRef.current = next;
        setDraft(next);
      } else if (activeTool === 'brush') {
        dragRef.current = { kind: 'brush' };
        const next: Annotation = {
          kind: 'brush',
          points: [point],
          color: ink,
          stroke: width,
        };
        draftRef.current = next;
        setDraft(next);
      } else if (activeTool === 'mosaic') {
        const color = sampleFrozenPixel(point);
        if (!color) {
          baselineRef.current = null;
          logCapture('mosaic sample is unavailable');
          return;
        }
        dragRef.current = { kind: 'mosaic' };
        const next: Annotation = {
          kind: 'mosaic',
          size: mosaicTileSize(width),
          tiles: [{ x: point.x, y: point.y, color }],
        };
        draftRef.current = next;
        setDraft(next);
      }
      setCursor(cursorFor(point, dragRef.current, current));
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }
    if (activeTool === 'move') {
      beginHistory();
      dragRef.current = { kind: 'new', origin: point };
      setCursor(cursorFor(point, dragRef.current, current));
      selectionRef.current = null;
      annotationsRef.current = [];
      draftRef.current = null;
      clearSelected();
      setLoupe(null);
      setSelection(null);
      setAnnotations([]);
      setDraft(null);
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event: ReactPointerEvent): void => {
    const point = pointOf(event);
    hoverRef.current = point;
    const drag = dragRef.current;
    setCursor(cursorFor(point, drag, selectionRef.current));
    if (selectionRef.current) {
      setLoupe(null);
    } else if (!drag) {
      setLoupe(point);
    }
    if (!drag) {
      return;
    }
    if (drag.kind === 'annotation') {
      if (!drag.moved) {
        if (!pointerMovedEnough(drag.origin, point)) {
          return;
        }
        drag.moved = true;
        beginHistory();
        setCursor(cursorFor(point, drag, selectionRef.current));
      }
      const dx = point.x - drag.last.x;
      const dy = point.y - drag.last.y;
      drag.last = point;
      const next = moveAnnotationAt(annotationsRef.current, drag.index, dx, dy);
      annotationsRef.current = next;
      setAnnotations(next);
      return;
    }
    if (drag.kind === 'new') {
      const next = normalizeRect(
        drag.origin.x,
        drag.origin.y,
        point.x,
        point.y,
      );
      selectionRef.current = next;
      setSelection(next);
      return;
    }
    if (drag.kind === 'move') {
      const dx = point.x - drag.last.x;
      const dy = point.y - drag.last.y;
      drag.last = point;
      setSelection((rect) => {
        const next = rect ? moveSelection(rect, dx, dy) : rect;
        selectionRef.current = next;
        return next;
      });
      setAnnotations((items) => {
        const next = translateAnnotations(items, dx, dy);
        annotationsRef.current = next;
        return next;
      });
      return;
    }
    if (drag.kind === 'resize') {
      setSelection((rect) => {
        const next = rect ? resizeSelection(rect, drag.handle, point) : rect;
        selectionRef.current = next;
        return next;
      });
      return;
    }
    if (drag.kind === 'shape-resize') {
      const item = annotationsRef.current[drag.index];
      if (!item || (item.kind !== 'rect' && item.kind !== 'ellipse')) {
        return;
      }
      const next = annotationsRef.current.map((entry, index) =>
        index === drag.index
          ? resizeBoxAnnotation(item, drag.handle, point)
          : entry,
      );
      annotationsRef.current = next;
      setAnnotations(next);
      return;
    }
    if (drag.kind === 'arrow-end') {
      const item = annotationsRef.current[drag.index];
      if (!item || item.kind !== 'arrow') {
        return;
      }
      const next = annotationsRef.current.map((entry, index) =>
        index === drag.index ? placeArrowEnd(item, drag.end, point) : entry,
      );
      annotationsRef.current = next;
      setAnnotations(next);
      return;
    }
    if (drag.kind === 'rect' || drag.kind === 'ellipse') {
      const rect = normalizeRect(
        drag.origin.x,
        drag.origin.y,
        point.x,
        point.y,
      );
      const next: Annotation = {
        kind: drag.kind,
        ...rect,
        color: colorRef.current,
        stroke: strokeRef.current,
      };
      draftRef.current = next;
      setDraft(next);
      return;
    }
    if (drag.kind === 'arrow') {
      const next: Annotation = {
        kind: 'arrow',
        x1: drag.origin.x,
        y1: drag.origin.y,
        x2: point.x,
        y2: point.y,
        color: colorRef.current,
        stroke: strokeRef.current,
      };
      draftRef.current = next;
      setDraft(next);
      return;
    }
    if (drag.kind === 'mosaic') {
      setDraft((item) => {
        if (!item || item.kind !== 'mosaic') {
          return item;
        }
        const next: Annotation = {
          ...item,
          tiles: extendMosaicTiles(
            item.tiles,
            point,
            item.size,
            sampleFrozenPixel,
          ),
        };
        draftRef.current = next;
        return next;
      });
      return;
    }
    setDraft((item) => {
      const next =
        item && item.kind === 'brush'
          ? { ...item, points: [...item.points, point] }
          : item;
      draftRef.current = next;
      return next;
    });
  };

  const onPointerUp = (): void => {
    const drag = dragRef.current;
    dragRef.current = null;
    setCursor(cursorFor(hoverRef.current, null, selectionRef.current));
    if (!drag) {
      return;
    }
    if (drag.kind === 'annotation') {
      if (drag.moved) {
        finishHistory();
        return;
      }
      const item = annotationsRef.current[drag.index];
      if (drag.tool === 'text' && item?.kind === 'text') {
        beginTextEdit(
          drag.index,
          item,
          item.text,
          item.color,
          item.fontSize,
          item.wrapWidth,
        );
      }
      return;
    }
    if (drag.kind === 'new') {
      const rect = selectionRef.current;
      if (rect && isUsableSelection(rect)) {
        finishHistory();
      } else {
        restoreBaseline();
      }
      return;
    }
    if (
      drag.kind === 'move' ||
      drag.kind === 'resize' ||
      drag.kind === 'shape-resize' ||
      drag.kind === 'arrow-end'
    ) {
      finishHistory();
      return;
    }
    if (
      drag.kind === 'rect' ||
      drag.kind === 'ellipse' ||
      drag.kind === 'arrow' ||
      drag.kind === 'brush' ||
      drag.kind === 'mosaic'
    ) {
      const item = draftRef.current;
      if (
        !item ||
        ((item.kind === 'rect' || item.kind === 'ellipse') &&
          (item.width < 2 || item.height < 2)) ||
        (item.kind === 'arrow' &&
          Math.hypot(item.x2 - item.x1, item.y2 - item.y1) < 2) ||
        (item.kind === 'mosaic' && item.tiles.length === 0)
      ) {
        baselineRef.current = null;
        draftRef.current = null;
        setDraft(null);
        return;
      }
      const before = takeSnapshot();
      finishHistoryWith({
        selection: before.selection,
        annotations: [...before.annotations, item],
      });
    }
  };

  const onLostPointerCapture = (event: ReactPointerEvent): void => {
    // Activation of a window that was not focused drops capture on the same
    // tick as pointerdown. Ending the gesture there throws the drag away
    // before it moves, which is the primary screen when another display was
    // shown last.
    if ((event.buttons & 1) !== 0) {
      return;
    }
    onPointerUp();
  };

  const onDoubleClick = (event: ReactMouseEvent): void => {
    if (tool === 'text' || textEditRef.current) {
      return;
    }
    const current = selectionRef.current;
    if (!current || !containsPoint(current, pointOf(event))) {
      return;
    }
    confirm();
  };

  const content = bitmapContentSize(imageWidth, imageHeight, scaleFactor);

  useLayoutEffect(() => {
    const bar = toolbarRef.current;
    if (!bar) {
      setToolbarSize((current) => (current ? null : current));
      return;
    }
    const rect = bar.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    setToolbarSize((current) => {
      if (current && current.width === width && current.height === height) {
        return current;
      }
      return { width, height };
    });
  }, [canRedo, canUndo, selection, textEdit]);

  const barStyle = selection
    ? toolbarStyle(selection, toolbarSize, {
        width: content.width,
        height: content.height,
      })
    : null;
  const optionAbove =
    barStyle != null &&
    barStyle.top +
      (toolbarSize?.height ?? 0) +
      TOOLBAR_GAP +
      OPTION_BAR_HEIGHT >
      content.height;

  return (
    <div
      className="fixed inset-0 bg-black text-white"
      onContextMenu={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <div
        ref={frameRef}
        className="relative"
        style={{ width: content.width, height: content.height }}
      >
        <img
          ref={imageRef}
          alt=""
          src={url}
          draggable={false}
          onLoad={(event) => {
            rememberPixels(event.currentTarget);
            onPainted();
          }}
          onError={onPaintFailed}
          style={{ width: content.width, height: content.height }}
          className="pointer-events-none max-w-none"
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 size-full touch-none"
          style={{ cursor }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onLostPointerCapture={onLostPointerCapture}
          onDoubleClick={onDoubleClick}
        />
        {selection ? null : (
          <p className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 rounded bg-black/70 px-3 py-1 text-sm">
            {text.hint}
          </p>
        )}
        {!selection && loupe ? (
          <Magnifier
            point={loupe}
            imageRef={imageRef}
            frameWidth={content.width}
            frameHeight={content.height}
            coordinatesLabel={text.coordinates}
            rgbLabel={text.rgb}
            copyColorLabel={text.copyColor}
            copyFailedLabel={text.copyFailed}
          />
        ) : null}
        {!selection && (canUndo || canRedo) ? (
          <div
            className={`absolute top-4 right-4 z-20 rounded-[var(--app-base-radius-12)] ${BAR_CLASS}`}
            style={{ ...BAR_CHROME, gap: 'var(--app-base-size-8)' }}
            onContextMenu={(event) => event.stopPropagation()}
          >
            <HistoryButtons
              canUndo={canUndo && !textEdit}
              canRedo={canRedo && !textEdit}
              undoLabel={text.undo}
              redoLabel={text.redo}
              onUndo={undo}
              onRedo={redo}
            />
          </div>
        ) : null}
        {textEdit && selection ? (
          <textarea
            ref={textInputRef}
            aria-label={text.text}
            autoFocus
            rows={1}
            spellCheck={false}
            value={textValue}
            onChange={(event) => {
              const next = event.target.value;
              textValueRef.current = next;
              setTextValue(next);
            }}
            onBlur={() => commitText()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                cancelText();
                return;
              }
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                commitText();
              }
              event.stopPropagation();
            }}
            style={{
              left: textEdit.x,
              top: textEdit.y,
              width: textColumnWidth(
                { x: textEdit.x, wrapWidth: textEdit.wrapWidth ?? undefined },
                selection,
              ),
              height: fontSize,
              color,
              fontSize,
              fontFamily: 'sans-serif',
              lineHeight: `${fontSize}px`,
              padding: 0,
              margin: 0,
              border: 'none',
              background: 'transparent',
              caretColor: color,
              resize: 'none',
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-all',
            }}
            className="absolute z-10 bg-transparent p-0 outline-none"
          />
        ) : null}
      </div>
      {selection && barStyle ? (
        <div
          ref={toolbarRef}
          className={`absolute z-20 rounded-[var(--app-base-radius-12)] ${BAR_CLASS}`}
          style={{ ...barStyle, ...BAR_CHROME, gap: 'var(--app-base-size-8)' }}
          onContextMenu={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <DrawToolButton
            toggle
            active={tool === 'rect'}
            label={text.rect}
            onClick={() => chooseTool('rect')}
          >
            <RectGlyph />
          </DrawToolButton>
          <DrawToolButton
            toggle
            active={tool === 'ellipse'}
            label={text.ellipse}
            onClick={() => chooseTool('ellipse')}
          >
            <EllipseGlyph />
          </DrawToolButton>
          <DrawToolButton
            toggle
            active={tool === 'arrow'}
            label={text.arrow}
            onClick={() => chooseTool('arrow')}
          >
            <ArrowGlyph />
          </DrawToolButton>
          <DrawToolButton
            toggle
            active={tool === 'brush'}
            label={text.brush}
            onClick={() => chooseTool('brush')}
          >
            <BrushGlyph />
          </DrawToolButton>
          <DrawToolButton
            toggle
            active={tool === 'text'}
            label={text.text}
            onClick={() => chooseTool('text')}
          >
            <TextGlyph />
          </DrawToolButton>
          <DrawToolButton
            toggle
            active={tool === 'mosaic'}
            label={text.mosaic}
            onClick={() => chooseTool('mosaic')}
          >
            <MosaicGlyph />
          </DrawToolButton>
          <ToolDivider />
          <HistoryButtons
            canUndo={canUndo && !textEdit}
            canRedo={canRedo && !textEdit}
            undoLabel={text.undo}
            redoLabel={text.redo}
            onUndo={undo}
            onRedo={redo}
          />
          <ToolDivider />
          <DrawToolButton active={false} label={text.copy} onClick={copy}>
            <CopyGlyph />
          </DrawToolButton>
          <DrawToolButton
            active={false}
            label={text.download}
            onClick={download}
          >
            <DownloadGlyph />
          </DrawToolButton>
          <DrawToolButton active={false} label={text.cancel} onClick={onCancel}>
            <CancelGlyph />
          </DrawToolButton>
          <DrawToolButton active={false} label={text.ok} onClick={confirm}>
            <OkGlyph />
          </DrawToolButton>
          <ToolOption
            open={tool !== 'move'}
            stroke={stroke}
            color={color}
            showColor={tool !== 'mosaic'}
            placeAbove={optionAbove}
            onStroke={chooseStroke}
            onColor={(value) => {
              colorRef.current = value;
              setColor(value);
            }}
          />
        </div>
      ) : null}
      {selection && barStyle && error ? (
        <p
          className="absolute z-20 text-xs text-[var(--app-color-danger)]"
          style={{
            left: barStyle.left,
            top:
              barStyle.top +
              (toolbarSize?.height ?? 0) +
              TOOLBAR_GAP +
              (tool !== 'move' && !optionAbove
                ? OPTION_BAR_HEIGHT + TOOLBAR_GAP
                : 0),
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * `421:37453` / `421:37456`. Outer size 20, corner radius 6.25 (bare).
 * Stroke is 1.5, the same weight as the other line icons. The frame's own
 * 1.6px border rasterizes heavier than those SVG strokes.
 */
function RectGlyph(): ReactNode {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="shrink-0"
    >
      <rect
        x="0.75"
        y="0.75"
        width="18.5"
        height="18.5"
        rx="5.5"
        stroke="var(--app-base-black)"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function EllipseGlyph(): ReactNode {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className="shrink-0"
    >
      <circle
        cx="10"
        cy="10"
        r="9.25"
        stroke="var(--app-base-black)"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function HistoryButtons({
  canUndo,
  canRedo,
  undoLabel,
  redoLabel,
  onUndo,
  onRedo,
}: {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string;
  redoLabel: string;
  onUndo: () => void;
  onRedo: () => void;
}) {
  return (
    <>
      <button
        type="button"
        aria-label={undoLabel}
        title={undoLabel}
        aria-keyshortcuts="Control+Z Meta+Z"
        disabled={!canUndo}
        onPointerDown={(event) => event.preventDefault()}
        onClick={onUndo}
        className={TOOL_BUTTON}
      >
        <UndoGlyph />
      </button>
      <button
        type="button"
        aria-label={redoLabel}
        title={redoLabel}
        aria-keyshortcuts="Control+Y Control+Shift+Z Meta+Shift+Z"
        disabled={!canRedo}
        onPointerDown={(event) => event.preventDefault()}
        onClick={onRedo}
        className={TOOL_BUTTON}
      >
        <RedoGlyph />
      </button>
    </>
  );
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable
  );
}

function DrawToolButton({
  active,
  toggle = false,
  label,
  onClick,
  children,
}: {
  active: boolean;
  toggle?: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      {...(toggle ? { 'aria-pressed': active } : {})}
      title={label}
      onPointerDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={
        active ? `${TOOL_BUTTON} bg-[var(--app-base-black-a4)]` : TOOL_BUTTON
      }
    >
      {children}
    </button>
  );
}

function ToolOption({
  open,
  stroke,
  color,
  showColor,
  placeAbove,
  onStroke,
  onColor,
}: {
  open: boolean;
  stroke: number;
  color: string;
  showColor: boolean;
  placeAbove: boolean;
  onStroke: (stroke: number) => void;
  onColor?: (color: string) => void;
}) {
  if (!open) {
    return null;
  }
  const inks = inkChoices();
  return (
    <div
      className={
        placeAbove
          ? `absolute bottom-full left-0 z-30 mb-[var(--app-base-size-6)] rounded-[var(--app-base-radius-12)] ${BAR_CLASS}`
          : `absolute top-full left-0 z-30 mt-[var(--app-base-size-6)] rounded-[var(--app-base-radius-12)] ${BAR_CLASS}`
      }
      style={{ ...BAR_CHROME, gap: 'var(--app-base-size-6)' }}
    >
      {STROKES.map((size) => (
        <button
          key={size}
          type="button"
          aria-label={String(size)}
          aria-pressed={size === stroke}
          title={String(size)}
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => onStroke(size)}
          className={TOOL_BUTTON}
        >
          <span
            className="rounded-full"
            style={{
              width: STROKE_DOT[size],
              height: STROKE_DOT[size],
              background:
                size === stroke
                  ? 'var(--app-base-brand)'
                  : 'var(--app-base-black-a60)',
            }}
          />
        </button>
      ))}
      {showColor ? (
        <>
          <ToolDivider />
          {inks.map((ink) => {
            const selected = ink.paint === color;
            return (
              <button
                key={ink.swatch}
                type="button"
                aria-label={ink.paint}
                aria-pressed={selected}
                title={ink.paint}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => onColor?.(ink.paint)}
                className={
                  selected
                    ? `${TOOL_BUTTON} bg-[var(--app-base-black-a4)]`
                    : TOOL_BUTTON
                }
              >
                <span
                  className="size-[23px] rounded-[6.988px]"
                  style={{
                    background: ink.swatch,
                    ...(ink.border
                      ? { border: `1px solid ${ink.border}` }
                      : {}),
                  }}
                />
              </button>
            );
          })}
        </>
      ) : null}
    </div>
  );
}

function handleCenter(rect: Rect, handle: Handle): Point {
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const midX = rect.x + rect.width / 2;
  const midY = rect.y + rect.height / 2;
  switch (handle) {
    case 'nw':
      return { x: rect.x, y: rect.y };
    case 'n':
      return { x: midX, y: rect.y };
    case 'ne':
      return { x: right, y: rect.y };
    case 'e':
      return { x: right, y: midY };
    case 'se':
      return { x: right, y: bottom };
    case 's':
      return { x: midX, y: bottom };
    case 'sw':
      return { x: rect.x, y: bottom };
    case 'w':
      return { x: rect.x, y: midY };
    default: {
      const unreachable: never = handle;
      return unreachable;
    }
  }
}

/** A mark stays grabbable after it leaves the selection. */
function annotationGrabIndex(
  selection: Rect,
  point: Point,
  annotations: readonly Annotation[],
  measure: (text: string, fontSize: number) => number,
): number | null {
  const index = hitAnnotationIndex(annotations, point, selection, measure);
  return index;
}

function pointerCursor(
  tool: Tool,
  selection: Rect | null,
  point: Point | null,
  drag: Drag | null,
  annotations: readonly Annotation[],
  measure: (text: string, fontSize: number) => number,
  selected: Annotation | null,
): string {
  if (drag?.kind === 'annotation') {
    return drag.moved ? 'grabbing' : 'grab';
  }
  if (drag?.kind === 'move') {
    return 'grabbing';
  }
  if (drag?.kind === 'resize' || drag?.kind === 'shape-resize') {
    return resizeCursor(drag.handle);
  }
  if (
    drag?.kind === 'new' ||
    drag?.kind === 'rect' ||
    drag?.kind === 'ellipse' ||
    drag?.kind === 'arrow' ||
    drag?.kind === 'arrow-end' ||
    drag?.kind === 'brush' ||
    drag?.kind === 'mosaic'
  ) {
    return 'crosshair';
  }
  if (!selection || !point) {
    return tool === 'move' ? 'crosshair' : 'default';
  }
  if (
    selected &&
    (selected.kind === 'rect' || selected.kind === 'ellipse') &&
    tool === selected.kind
  ) {
    const handle = hitHandle(selected, point, HANDLE_HIT_RADIUS);
    if (handle) {
      return resizeCursor(handle);
    }
  }
  if (selected?.kind === 'arrow' && tool === 'arrow') {
    if (hitArrowEnd(selected, point, HANDLE_HIT_RADIUS)) {
      return 'crosshair';
    }
  }
  if (
    tool !== 'move' &&
    annotationGrabIndex(selection, point, annotations, measure) !== null
  ) {
    return 'grab';
  }
  if (tool === 'move') {
    const handle = hitHandle(selection, point, HANDLE_HIT_RADIUS);
    if (handle) {
      return resizeCursor(handle);
    }
    return containsPoint(selection, point) ? 'move' : 'crosshair';
  }
  if (tool === 'text') {
    return containsPoint(selection, point) ? 'text' : 'default';
  }
  return containsPoint(selection, point) ? 'crosshair' : 'default';
}

function resizeCursor(handle: Handle): string {
  switch (handle) {
    case 'n':
    case 's':
      return 'ns-resize';
    case 'e':
    case 'w':
      return 'ew-resize';
    case 'ne':
    case 'sw':
      return 'nesw-resize';
    case 'nw':
    case 'se':
      return 'nwse-resize';
    default: {
      const unreachable: never = handle;
      return unreachable;
    }
  }
}

function toolbarStyle(
  selection: Rect,
  toolbar: { width: number; height: number } | null,
  frame: { width: number; height: number },
): { left: number; top: number; visibility: 'hidden' | 'visible' } {
  const width = toolbar?.width ?? 0;
  const height = toolbar?.height ?? 0;
  const left = Math.min(
    Math.max(0, selection.x + selection.width - width),
    Math.max(0, frame.width - width),
  );
  let top = selection.y + selection.height + TOOLBAR_GAP;
  if (top > frame.height - height) {
    top = frame.height - height - TOOLBAR_GAP;
  }
  if (top < 0) {
    top = 0;
  }
  return {
    left,
    top,
    visibility: toolbar ? 'visible' : 'hidden',
  };
}

function drawHandleDots(ctx: CanvasRenderingContext2D, rect: Rect): void {
  for (const handle of ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const) {
    drawHandleDot(ctx, handleCenter(rect, handle));
  }
}

interface LoupeColor {
  red: number;
  green: number;
  blue: number;
}

function Magnifier({
  point,
  imageRef,
  frameWidth,
  frameHeight,
  coordinatesLabel,
  rgbLabel,
  copyColorLabel,
  copyFailedLabel,
}: {
  point: Point;
  imageRef: RefObject<HTMLImageElement | null>;
  frameWidth: number;
  frameHeight: number;
  coordinatesLabel: string;
  rgbLabel: string;
  copyColorLabel: string;
  copyFailedLabel: string;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const colorRef = useRef<LoupeColor | null>(null);
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const [color, setColor] = useState<LoupeColor | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  colorRef.current = color;

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) {
      return;
    }
    const rect = box.getBoundingClientRect();
    let x = point.x + LOUPE_GAP;
    let y = point.y + LOUPE_GAP;
    if (x + rect.width > frameWidth) {
      x = point.x - rect.width - LOUPE_GAP;
    }
    if (y + rect.height > frameHeight) {
      y = point.y - rect.height - LOUPE_GAP;
    }
    setAt({
      x: Math.max(0, x),
      y: Math.max(0, y),
    });
  }, [frameHeight, frameWidth, point.x, point.y]);

  useEffect(() => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (!image || !canvas || !(image.naturalWidth > 0) || !(frameWidth > 0)) {
      setColor(null);
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setColor(null);
      return;
    }
    const rx = image.naturalWidth / frameWidth;
    const ry = image.naturalHeight / frameHeight;
    ctx.clearRect(0, 0, LOUPE_SIZE, LOUPE_SIZE);
    ctx.drawImage(
      image,
      point.x * rx - LOUPE_SIZE / 2,
      point.y * ry - LOUPE_SIZE / 2,
      LOUPE_SIZE,
      LOUPE_SIZE,
      0,
      0,
      LOUPE_SIZE,
      LOUPE_SIZE,
    );
    const pixel = ctx.getImageData(
      Math.floor(LOUPE_SIZE / 2),
      Math.floor(LOUPE_SIZE / 2),
      1,
      1,
    ).data;
    const red = pixel[0];
    const green = pixel[1];
    const blue = pixel[2];
    if (red === undefined || green === undefined || blue === undefined) {
      setColor(null);
      return;
    }
    setColor({ red, green, blue });
  }, [frameHeight, frameWidth, imageRef, point.x, point.y]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.isComposing || event.repeat) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      if (event.key.toLowerCase() !== 'c') {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }
      const current = colorRef.current;
      if (!current) {
        return;
      }
      event.preventDefault();
      const value = `rgb(${current.red}, ${current.green}, ${current.blue})`;
      const clipboard = captureHostWindow().hibroClipboard;
      if (!clipboard) {
        logCapture(
          'capture color copy failed',
          new Error('hibroClipboard.writeText is missing'),
        );
        setCopyError(copyFailedLabel);
        return;
      }
      void clipboard.writeText(value).then(
        (ok) => {
          if (!ok) {
            logCapture(
              'capture color copy failed',
              new Error('clipboard refused the color'),
            );
            setCopyError(copyFailedLabel);
            return;
          }
          setCopyError(null);
        },
        (error: unknown) => {
          logCapture('capture color copy failed', error);
          setCopyError(copyFailedLabel);
        },
      );
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [copyFailedLabel]);

  const x = Math.round(point.x);
  const y = Math.round(point.y);

  return (
    <div
      ref={boxRef}
      className="pointer-events-none absolute z-20 box-border flex w-[137.2px] flex-col overflow-hidden rounded-[var(--app-base-radius-8)] border-[length:var(--app-base-stroke-0-6)] border-solid border-[var(--app-base-gray-77)]"
      style={{
        left: at?.x ?? point.x + LOUPE_GAP,
        top: at?.y ?? point.y + LOUPE_GAP,
        visibility: at ? 'visible' : 'hidden',
      }}
    >
      <div className="relative size-[136px] shrink-0">
        <canvas
          ref={canvasRef}
          width={LOUPE_SIZE}
          height={LOUPE_SIZE}
          className="block size-full"
        />
        <span
          className="absolute inset-x-0 bg-[var(--app-base-brand)]"
          style={{
            top: LOUPE_CROSS_Y,
            height: 'var(--app-base-stroke-1)',
            transform: 'translateY(-50%)',
          }}
        />
        <span
          className="absolute inset-y-0 bg-[var(--app-base-brand)]"
          style={{
            left: LOUPE_CROSS_X,
            width: 'var(--app-base-stroke-1)',
            transform: 'translateX(-50%)',
          }}
        />
      </div>
      {/*
        `421:36248`. Acrylic blur radius 60 is CSS 30px. Blur is omitted on
        this overlay; `Color/background/pop/pop S` is raised from 0.85 to 0.95.
        There is no 0.95 token. `leading` 16px is `Font/LineH/16` (no token).
      */}
      <div
        className="flex w-full flex-col items-center gap-[var(--app-base-size-2)] px-[var(--app-base-size-5)] py-[var(--app-base-size-4)] text-center font-[family-name:var(--app-base-font-family)] text-[length:var(--app-base-font-size-11)] leading-[16px] font-normal text-[var(--app-base-black)]"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
      >
        <p className="whitespace-nowrap">
          {coordinatesLabel}: {x}, {y}
        </p>
        {color ? (
          <div className="flex items-center gap-[var(--app-base-size-3)]">
            <span
              className="size-[var(--app-base-size-7)] shrink-0 rounded-[2px] border-[length:var(--app-base-stroke-0-6)] border-solid border-[var(--app-base-gray-86)]"
              style={{
                backgroundColor: `rgb(${color.red}, ${color.green}, ${color.blue})`,
              }}
            />
            <p className="whitespace-nowrap">
              {rgbLabel}: {color.red}, {color.green}, {color.blue}
            </p>
          </div>
        ) : null}
        <p
          className={`whitespace-nowrap ${copyError ? 'text-[var(--app-color-danger)]' : ''}`}
        >
          {copyError ?? copyColorLabel}
        </p>
      </div>
    </div>
  );
}

function drawHandleDot(ctx: CanvasRenderingContext2D, at: Point): void {
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#0090ff';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(at.x, at.y, HANDLE_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}
